import React from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Grow,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip, Typography
} from "@mui/material";
import {visuallyHidden} from '@mui/utils';
import PropTypes from 'prop-types';
import AttendanceData from "../../backend/dataservices/AttendanceData";
import CourseData from "../../backend/dataservices/CourseData";
import {useNavigate} from "react-router-dom";
import EmployeeData from "../../backend/dataservices/EmployeeData";
import CreateNewAttendanceDialogComponent from "../CreateNewAttendanceDialogComponent";
import MatrixTableFunctionsRow from "../MatrixTableFunctionsRow";
import UserData from "../../backend/dataservices/UserSettings";
import TableUtils from "../../utils/TableUtils";

function createData(employeeName, employeeID) {
    return {employeeName, employeeID};
}

let minColumnWidth = 150;
let minEmployeeColumnWidth = 250;

function descendingComparator(a, b, orderBy) {
    if (orderBy === 'date') {
        if (b[orderBy].length >= 11 && a[orderBy].length >= 11) {
            if (b[orderBy].substring(6, 10) < a[orderBy].substring(6, 10)) {
                return -1;
            }
            if (b[orderBy].substring(6, 10) > a[orderBy].substring(6, 10)) {
                return 1;
            }
            return 0;
        }
    }
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;

}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
    const {order, onRequestSort} =
        props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow sx={{borderBottom: '2px solid gray'}}>
                {props.headCells.map((headCell) => (
                    <TableCell
                        style={{minWidth: headCell.minWidth, zIndex: 'employeeName' === headCell.id ? 2: 1, position: 'employeeName' === headCell.id ? 'sticky' : null, left:'employeeName' === headCell.id ? 0 : minEmployeeColumnWidth, backgroundColor: 'white'}}
                        key={headCell.id}
                        align={headCell.numeric ? 'center' : 'left'}
                        padding={'none'}
                        sortDirection={'employeeName' === headCell.id ? order : false}
                    >
                        {'employeeName' === headCell.id ?
                            <TableSortLabel
                                active={'employeeName' === headCell.id}
                                direction={'employeeName' === headCell.id ? order : 'asc'}
                                onClick={'employeeName' === headCell.id ? createSortHandler(headCell.id) : null}
                            >
                                <Typography textOverflow={'ellipsis'} overflow={'hidden'}>{headCell.label}</Typography>
                                {'employeeName' === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                            :
                            <Tooltip title={"Doppelklick, um Kurs zu entfernen"}>
                                <Button sx={{color: "black"}} fullWidth onDoubleClick={() => {
                                    props.removeHeadCell(headCell.id);
                                }}>
                                    {headCell.label}
                                </Button>
                            </Tooltip>
                        }
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
    removeHeadCell: PropTypes.func.isRequired,
};

export default function MatrixTable({transformLayout, saveLayout}) {


    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('employeeName');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(100);
    const [rows, setRows] = React.useState([]);
    const [myHeadCells, setMyHeadCells] = React.useState([]);
    const [attsSet, setAttsSet] = React.useState(new Set());
    const [newAttendanceData, setNewAttendanceData] = React.useState({});
    const [openNewAttendanceDialog, setOpenNewAttendanceDialog] = React.useState(false);
    const [openDialog, setOpenDialog] = React.useState(false);
    const [dialogData, setDialogData] = React.useState({});
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarText, setSnackbarText] = React.useState("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");
    const navigate = useNavigate();

    let headCells = [
        {
            id: 'employeeName',
            numeric: false,
            disablePadding: false,
            label: 'Mitarbeiter',
            minWidth: minEmployeeColumnWidth,
        },
    ]

    React.useEffect(() => {
        (async () => {
            loadRows(true);
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    function newCourseIsNotUsed(courseID) {
        let found = true;
        headCells.forEach(headCell => {
           if(headCell.id === courseID)
               found = false;
        });
        return found;
    }

    function loadAttsForCoursesAndEmployees(tempRows, courses, counter, tempAttsSet, initData) {
        EmployeeData.getAllEmployees().then(employees => {
            if (!Array.isArray(employees)) return;
            employees.forEach(employee => {
                tempRows.push(createData(employee.M_Name + ', ' + employee.M_Vorname, employee["M_Pers-Nr"]))
            });
            if(courses == null || courses.length === 0) {
                setMyHeadCells(headCells);
                setRows(tempRows);
                return;
            }
            courses.forEach(courseID => {
                CourseData.fetchCourseBaseData(courseID).then(course => {
                    if (newCourseIsNotUsed(courseID)) {
                        headCells.push({
                            id: courseID,
                            numeric: true,
                            disablePadding: false,
                            label: course["KS_Kursname"],
                            minWidth: minColumnWidth
                        });
                    }
                    AttendanceData.fetchAllAttendancesForCourse(courseID).then(atts => {
                        if (!Array.isArray(atts)) atts = [];
                        atts.forEach(att => {
                            att.KS_ID = courseID
                        });
                        counter++;
                        tempAttsSet.add(atts);
                        if (counter === courses.length) {
                            headCells.sort((a, b) => a.id > b.id)
                            setMyHeadCells(initData ? headCells : myHeadCells);
                            setAttsSet(tempAttsSet);
                            setRows(tempRows);
                        }
                    });
                });
            });
        });
    }

    function loadRows(initData) {
        let tempRows = [];
        let tempAttsSet = new Set();
        let counter = 0;
        let courses = [];
        if(initData) {
            UserData.fetchMatrixUserSettings().then(settings => {
                courses = settings["courses"];
                loadAttsForCoursesAndEmployees(tempRows, courses, counter, tempAttsSet, initData);
            });
        } else {
            myHeadCells.forEach(hc => {
                courses.push(hc.id);
            });
            loadAttsForCoursesAndEmployees(tempRows, courses, counter, tempAttsSet, initData);
        }
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };


    function toEmployees(employee, employeeID) {
        navigate("/employees", {state: {id: employeeID, name: employee}});
    }

    function closeSnackbar() {
        setSnackbarOpen(false);
    }

    function handleCloseNewAttendanceDialog() {
        setOpenNewAttendanceDialog(false);
    }

    function handleCloseDialog() {
        setOpenDialog(false);
    }

    function addCourse(courseID, name) {
        if(!courseAlreadySelected(courseID)) {
            let hc = myHeadCells;
            hc.push({
                id: courseID,
                numeric: true,
                disablePadding: false,
                label: name,
                minWidth: minColumnWidth,
            });
            setMyHeadCells(hc);
            loadRows(false);
        } else {
            setSnackbarSeverity("error");
            setSnackbarText("Kurs wird bereits angezeigt");
            setSnackbarOpen(true);
        }
    }

    function courseAlreadySelected(courseID) {
        let taken = false;
        myHeadCells.forEach(hc => {
            if(hc.id ===courseID) {
                taken = true;
            }
        });
        return taken;
    }

    function removeHeadCell(id) {
        let newCells = [];
        for (const index in myHeadCells) {
            let cell = myHeadCells[index];
            if (cell.id !== id) {
                newCells.push(cell);
            }
        }
        setMyHeadCells(newCells);
    }

    function handleTableCellClick(courseID, employeeID, employeeName, courseName, status, year, attendanceID) {
        setDialogData({
            courseID: courseID,
            employeeID: employeeID,
            employeeName: employeeName,
            courseName: courseName,
            status: status,
            year: year,
            attendanceID: attendanceID
        });
        if (status === 'Geplant') {
            setOpenDialog(true);
        }
    }

    function deleteAttendance(attendanceID) {
        handleCloseDialog();
        AttendanceData.deletePlannedAttendance(attendanceID).then(value => {
            setSnackbarSeverity("success");
            setSnackbarText("Planung erfoglreich gelöscht");
            setSnackbarOpen(true);
        }, (error) => {
            setSnackbarSeverity("error");
            setSnackbarText("Ein unerwarteter Fehler ist aufgetreten");
            setSnackbarOpen(true);
        }).then(value => {
            loadRows(false)
        })
    }

    function applyAttendance(attendanceID) {
        handleCloseDialog();
        AttendanceData.updatePlannedAttendance(attendanceID, 'Beantragt').then(value => {
            setSnackbarSeverity("success");
            setSnackbarText("Kurs erfolgreich beantragt");
            setSnackbarOpen(true);
        }, (error) => {
            setSnackbarSeverity("error");
            setSnackbarText("Ein unerwarteter Fehler ist aufgetreten");
            setSnackbarOpen(true);
        }).then(value => {
            loadRows(false)
        })
    }

    return (
        <Grid container spacing={2}
              alignItems="center"
              justifyContent="center">
            <Grid item xs={12}>
                <MatrixTableFunctionsRow addCourse={function (courseID, name) {
                    addCourse(courseID, name);
                    setMyHeadCells(myHeadCells);
                }} transformLayout={function () {
                    transformLayout();
                }} saveLayout={function () {
                    let courses = [];
                    for(const index in myHeadCells) {
                        if(index > 0) {
                            courses.push(myHeadCells[index].id);
                        }
                    }
                    saveLayout(courses);
                }}/>
            </Grid>

            <Grid item xs={11} maxHeight={600}>
                <Paper sx={{width: '100%', mb: 2, maxHeight: 'inherit'}}>

                    <TableContainer sx={{maxHeight: 'inherit'}}>
                        <Table
                            stickyHeader
                            sx={{alignItems: 'center', alignContent: 'center'}}
                            aria-labelledby="tableTitle"
                            size={'medium'}
                        >
                            <EnhancedTableHead
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                rowCount={rows.length}
                                headCells={myHeadCells}
                                removeHeadCell={removeHeadCell}
                            />
                            <TableBody>
                                {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                     suggestions.slice().sort(getComparator(order, orderBy)) */}
                                {stableSort(rows, getComparator(order, orderBy))
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => {
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        return (
                                            <TableRow
                                                key={row.employeeID}
                                                hover
                                                tabIndex={-1}
                                                sx={{height: 50}}
                                            >
                                                <TableCell
                                                    sx={{border: '1px solid lightgrey', position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'white'}}
                                                    component="th"
                                                    id={labelId}
                                                    scope="row"
                                                    padding="none"
                                                    style={{cursor: "pointer"}}
                                                    onClick={() => toEmployees(row.employeeName, row.employeeID)}
                                                >
                                                    {row.employeeName}
                                                </TableCell>

                                                {myHeadCells.filter(function (value, index, array) {
                                                    return index > 0;
                                                }).map(function (value, index, array) {
                                                    return TableUtils.getTableCell(value.id, row.employeeID, row.employeeName, myHeadCells[index + 1].label, attsSet,
                                                        function (courseID, employeeID, employeeName, courseName, status, year, attendanceID) {
                                                        handleTableCellClick(courseID, employeeID, employeeName, courseName, status, year, attendanceID)
                                                        }, function (data) {
                                                            setNewAttendanceData(data);
                                                            setOpenNewAttendanceDialog(true);
                                                        });
                                                })
                                                }


                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20, 50, 100]}
                        component="div"
                        count={rows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Grid>

            <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={closeSnackbar}
                      anchorOrigin={{vertical: "top", horizontal: "right"}} TransitionComponent={Grow}>
                <Alert onClose={closeSnackbar} severity={snackbarSeverity} sx={{width: '100%'}}>
                    {snackbarText}
                </Alert>
            </Snackbar>


            <CreateNewAttendanceDialogComponent
                openDialog={openNewAttendanceDialog}
                handleClose={handleCloseNewAttendanceDialog}
                handleSuccess={() => {
                    setOpenNewAttendanceDialog(false);
                    setSnackbarText("Neue Kursanmeldung für " + newAttendanceData.employeeName + " erfolgreich!");
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                    loadRows(false);
                    // handleInputValue(suggestionValue);
                }}
                handleError={() => {
                    setOpenNewAttendanceDialog(false);
                    setSnackbarText("Bei der Kursanmeldung ist ein unerwarteter Fehler aufetreten")
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                }}
                knownCourseAndEmployee={true} knownData={newAttendanceData}/>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle sx={{backgroundColor: 'rgb(0,150,136)'}} color={"white"}>
                    Neue Kursanmeldung
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} marginTop={'5%'}>
                        <Grid item xs={12}>
                            Kurs: {dialogData.courseName}
                        </Grid>
                        <Grid item xs={12}>
                            Name: {dialogData.employeeName}
                        </Grid>
                        <Grid item xs={12}>
                            Status: {dialogData.status}
                        </Grid>
                        <Grid item xs={12}>
                            Datum: {dialogData.year}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button style={{color: "gray"}} onClick={handleCloseDialog}>Abbrechen</Button>
                    <Button style={{color: "red"}} onClick={() => deleteAttendance(dialogData.attendanceID)}>Planung
                        löschen</Button>
                    <Button onClick={() => applyAttendance(dialogData.attendanceID)}>Planung beantragen</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}

